import { useExecutiveSummaryStore } from '~/stores/projectDataVisualizations/executiveSummary'
import { useSignalREvents } from './useSignalR'
import {
  SIGNALR_EVENTS,
  type NotifyStandardExportProgressData,
} from '~/constants/signalREvents'
import { EnumerationsExecutiveSummaryStatus } from 'client/sratool'
import { useProjectStore } from '~/stores/projects'

export const useExecutiveSummary = () => {
  const signalR = useSignalREvents()
  const ExecutiveSummaryStore = useExecutiveSummaryStore()
  const ProjectStore = useProjectStore()

  const handleProgress: (...args: NotifyStandardExportProgressData) => void = (
    notificationID,
    downloadID,
    percentage,
    status,
    stepDescription
  ) => {
    const formattedStatus: EnumerationsExecutiveSummaryStatus = Object.keys(
      EnumerationsExecutiveSummaryStatus
    ).includes(status)
      ? EnumerationsExecutiveSummaryStatus[status]
      : undefined

    const projectID =
      ExecutiveSummaryStore.getSummaryInformationProjectIDByNotificationID(
        notificationID
      )

    if (!projectID) {
      return
    }

    ExecutiveSummaryStore.setSummaryInformation(
      {
        status: formattedStatus,
        percent: percentage,
        currentStepDescription: stepDescription,
      },
      projectID
    )
  }

  const handleCompleted = async (notificationID: string) => {
    const projectID =
      ExecutiveSummaryStore.getSummaryInformationProjectIDByNotificationID(
        notificationID
      )

    if (!projectID) {
      return
    }

    ExecutiveSummaryStore.setSummaryInformation(
      {
        status: EnumerationsExecutiveSummaryStatus.Completed,
        percent: 1,
      },
      projectID
    )

    if (
      notificationID ===
        ExecutiveSummaryStore.summaryInformationByProjectID?.notificationID &&
      ProjectStore.selectedProject
    ) {
      await ExecutiveSummaryStore.getSettings({
        projectID: ProjectStore.selectedProject.projectID,
        checkSummaryCompletion: true,
      })
    }
  }

  const handleFailed = (notificationID: string) => {
    const projectID =
      ExecutiveSummaryStore.getSummaryInformationProjectIDByNotificationID(
        notificationID
      )

    if (!projectID) {
      return
    }

    ExecutiveSummaryStore.setSummaryInformation(
      {
        status: EnumerationsExecutiveSummaryStatus.Failed,
        percent: 0,
        currentStepDescription: undefined,
        fileName: undefined,
        fileSize: undefined,
        notificationID: undefined,
      },
      projectID
    )
  }

  const handleOn = () => {
    signalR.on(
      SIGNALR_EVENTS.EXPORT_HUB.NOTIFY_STANDARD_EXPORT_PROGRESS,
      handleProgress
    )
    signalR.on(
      SIGNALR_EVENTS.EXPORT_HUB.NOTIFY_STANDARD_EXPORT_COMPLETED,
      handleCompleted
    )
    signalR.on(
      SIGNALR_EVENTS.EXPORT_HUB.NOTIFY_STANDARD_EXPORT_FAILED,
      handleFailed
    )
  }

  const handleOff = () => {
    signalR.off(
      SIGNALR_EVENTS.EXPORT_HUB.NOTIFY_STANDARD_EXPORT_PROGRESS,
      handleProgress
    )
    signalR.off(
      SIGNALR_EVENTS.EXPORT_HUB.NOTIFY_STANDARD_EXPORT_COMPLETED,
      handleCompleted
    )
    signalR.off(
      SIGNALR_EVENTS.EXPORT_HUB.NOTIFY_STANDARD_EXPORT_FAILED,
      handleFailed
    )
  }

  onMounted(() => {
    handleOn()
  })

  onUnmounted(() => {
    handleOff()
  })

  return {
    handleOn,
    handleOff,
  }
}
